import { useEffect, useState, type FormEvent } from "react";
import { SyntaxCode } from "@/office/SyntaxCode";
import { cloudFile, cloudLogin, readCloudToken, saveCloudToken } from "@/system/cloud";
import { focusWindow } from "@/system/modules";
import featureMap from "@/system/feature-map.json";

type FrontSite = { file: string; line: number };
type Feature = {
  id: string;
  app: string;
  page: string;
  name: string;
  route: string;
  method: string;
  front: FrontSite[];
  server: string;
  utils: string[];
  tables: string[];
};
type PageInfo = { id: string; name: string; file: string; route?: string };
type AppInfo = { id: string; name: string; dir: string; pages: PageInfo[] };

const MAP = featureMap as unknown as {
  apps: AppInfo[];
  features: Feature[];
  byTable: Record<string, string[]>;
  byFile: Record<string, string[]>;
  duplicates: Record<string, string[]>;
};

const FEATURES = MAP.features;
const BY_ID = new Map(FEATURES.map((f) => [f.id, f]));

function short(path: string): string {
  const parts = path.split("/");
  return parts.slice(-2).join("/");
}

function baseName(path: string): string {
  return path.split("/").pop() ?? path;
}

/** 该功能所在页面之外，还有哪些功能会受同一张表影响 */
function byTableOthers(table: string, self: string): Feature[] {
  return (MAP.byTable[table] ?? []).filter((id) => id !== self).map((id) => BY_ID.get(id)).filter((f): f is Feature => Boolean(f));
}

function byUtilsOthers(file: string, self: string): Feature[] {
  return (MAP.byFile[file] ?? []).filter((id) => id !== self).map((id) => BY_ID.get(id)).filter((f): f is Feature => Boolean(f));
}

type CodeView = { path: string; line: number | null; code: string; lines: number } | null;

/** 默认选中：先挑“动数据最多”的功能，打开页面时不至于落在兜底路由上 */
function defaultFeature(appId: string): Feature | null {
  const list = FEATURES.filter((f) => f.app === appId);
  const rich = list.filter((f) => f.tables.length >= 2 && f.front.length > 0);
  rich.sort((a, b) => b.tables.length - a.tables.length);
  return rich[0] ?? list[0] ?? null;
}

export function FeatureMapPage() {
  const [appId, setAppId] = useState<string>(() => localStorage.getItem("jhzz_map_app") ?? MAP.apps[0]?.id ?? "");
  const [openPages, setOpenPages] = useState<string[]>(() => {
    const start = defaultFeature(localStorage.getItem("jhzz_map_app") ?? MAP.apps[0]?.id ?? "");
    return start?.page ? [start.page] : [];
  });
  const [selected, setSelected] = useState<string>(() => {
    const start = defaultFeature(localStorage.getItem("jhzz_map_app") ?? MAP.apps[0]?.id ?? "");
    return start?.id ?? "";
  });
  const [keyword, setKeyword] = useState("");
  const [view, setView] = useState<CodeView>(null);
  const [loadingCode, setLoadingCode] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [token, setToken] = useState(() => readCloudToken());
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const app = MAP.apps.find((a) => a.id === appId) ?? MAP.apps[0];
  const current = selected ? BY_ID.get(selected) ?? null : null;

  useEffect(() => {
    localStorage.setItem("jhzz_map_app", appId);
  }, [appId]);

  function pick(id: string) {
    setSelected(id);
    setCodeError("");
  }

  async function openCode(path: string, line: number | null) {
    setLoadingCode(true);
    setCodeError("");
    try {
      const file = await cloudFile(path);
      setView({ path, line, code: file.code, lines: file.lines });
    } catch (error) {
      setCodeError(error instanceof Error ? error.message : "读不到这个文件");
    } finally {
      setLoadingCode(false);
    }
  }

  async function doLogin(event: FormEvent) {
    event.preventDefault();
    setLoginError("");
    try {
      await cloudLogin(password);
      saveCloudToken(readCloudToken());
      setToken(readCloudToken());
      setPassword("");
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "口令不对");
    }
  }

  // 树：页面 -> 功能
  const groups = [
    ...app.pages.map((page) => ({
      id: page.id,
      name: page.name,
      items: FEATURES.filter((f) => f.app === app.id && f.page === page.id),
    })),
    {
      id: "_houduan",
      name: "后台自动跑 / 公共",
      items: FEATURES.filter((f) => f.app === app.id && !f.page),
    },
  ].filter((group) => group.items.length > 0);

  const hit = keyword.trim()
    ? FEATURES.filter(
        (f) =>
          f.name.includes(keyword) ||
          f.route.includes(keyword) ||
          f.tables.some((t) => t.includes(keyword)) ||
          f.server.includes(keyword),
      ).slice(0, 40)
    : null;

  const windowed = view && view.line ? focusWindow(view.code, view.line) : null;

  return (
    <div className="flex h-screen flex-col bg-[#0a1017] text-[#cfe3f5]">
      <header className="flex shrink-0 items-center gap-3 border-b border-[#16222e] px-4 py-2.5">
        <span className="text-[13.5px] font-medium">功能地图</span>
        <span className="hidden text-[11.5px] text-[#6e8599] sm:inline">
          左边选一个功能，右边告诉你：它是哪几段代码做的、会动哪些数据、改了会连带影响谁
        </span>
        <a href="#/" className="ml-auto shrink-0 rounded border border-[#24384a] px-2.5 py-1 text-[11.5px] text-[#9db3c7] hover:border-[#3a5a74] hover:text-[#cfe3f5]">
          返回点选对照
        </a>
      </header>

      <div className="flex shrink-0 items-center gap-2 border-b border-[#16222e] bg-[#0b141c] px-4 py-2">
        {MAP.apps.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              setAppId(item.id);
              const first = defaultFeature(item.id);
              setOpenPages(first?.page ? [first.page] : []);
              if (first) pick(first.id);
            }}
            className={`rounded px-2.5 py-1 text-[12px] transition-colors ${
              appId === item.id ? "bg-[#17242f] text-[#cfe3f5] ring-1 ring-[#27455f]" : "text-[#69809a] hover:text-[#a9bdcd]"
            }`}
          >
            {item.name}
          </button>
        ))}
        <input
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder="搜功能名、接口名、表名（如 生成工资 / salary_salary）"
          className="ml-2 w-72 rounded border border-[#24384a] bg-[#0d1720] px-2 py-1 text-[12px] text-[#cfe3f5] outline-none placeholder:text-[#4e6478] focus:border-[#3a5a74]"
        />
        {!token ? (
          <form onSubmit={doLogin} className="ml-auto flex items-center gap-1.5">
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              placeholder="云电脑口令"
              className="w-32 rounded border border-[#24384a] bg-[#0d1720] px-2 py-1 text-[12px] outline-none placeholder:text-[#4e6478]"
            />
            <button type="submit" className="rounded border border-[#24384a] px-2 py-1 text-[11.5px] text-[#9db3c7] hover:text-[#cfe3f5]">
              连接
            </button>
            {loginError ? <span className="text-[11px] text-[#c0503a]">{loginError}</span> : null}
          </form>
        ) : (
          <span className="ml-auto text-[11px] text-[#4e9d6a]">已连上云电脑，可展开看代码</span>
        )}
      </div>

      <div className="flex min-h-0 flex-1">
        {/* 左：功能树 */}
        <aside className="w-[292px] shrink-0 overflow-auto border-r border-[#16222e] bg-[#0b141c] py-2">
          {hit ? (
            <div className="px-2">
              <p className="px-1 pb-1 text-[11px] text-[#69809a]">搜到 {hit.length} 个功能</p>
              {hit.map((feature) => (
                <button
                  key={feature.id}
                  type="button"
                  onClick={() => pick(feature.id)}
                  className={`block w-full truncate rounded px-2 py-1 text-left text-[12px] ${
                    selected === feature.id ? "bg-[#17242f] text-[#cfe3f5]" : "text-[#9db3c7] hover:bg-[#121c26]"
                  }`}
                >
                  {feature.name}
                  <span className="ml-1 text-[10.5px] text-[#4e6478]">{feature.tables[0] ?? ""}</span>
                </button>
              ))}
            </div>
          ) : (
            groups.map((group) => {
              const open = openPages.includes(group.id);
              return (
                <div key={group.id} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenPages(open ? openPages.filter((id) => id !== group.id) : [...openPages, group.id])
                    }
                    className="flex w-full items-center gap-1 px-3 py-1.5 text-left text-[12px] text-[#a9bdcd] hover:bg-[#121c26]"
                  >
                    <span className="text-[10px] text-[#4e6478]">{open ? "▾" : "▸"}</span>
                    <span className="truncate">{group.name}</span>
                    <span className="ml-auto shrink-0 text-[10.5px] text-[#4e6478]">{group.items.length}</span>
                  </button>
                  {open
                    ? group.items.map((feature) => (
                        <button
                          key={feature.id}
                          type="button"
                          onClick={() => pick(feature.id)}
                          className={`block w-full truncate py-1 pl-8 pr-2 text-left text-[12px] ${
                            selected === feature.id ? "bg-[#17242f] text-[#cfe3f5]" : "text-[#8aa0b5] hover:bg-[#121c26]"
                          }`}
                        >
                          {feature.name}
                        </button>
                      ))
                    : null}
                </div>
              );
            })
          )}
        </aside>

        {/* 右：功能详情 */}
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
            {!current ? (
              <p className="text-[12.5px] text-[#9db3c7]">左边选一个功能。</p>
            ) : (
              <FeatureDetail
                feature={current}
                onPick={pick}
                onOpenCode={openCode}
                codeHint={codeError || (loadingCode ? "读取中…" : "")}
              />
            )}
          </div>

          {view ? (
            <div className="flex h-[42%] shrink-0 flex-col border-t border-[#16222e] bg-[#080f16]">
              <div className="flex shrink-0 items-center gap-2 border-b border-[#16222e] px-4 py-1.5">
                <span className="min-w-0 truncate font-mono text-[11px] text-[#7fb3ff]">{view.path}</span>
                <span className="shrink-0 text-[10.5px] text-[#4e6478]">共 {view.lines} 行</span>
                {windowed ? (
                  <button
                    type="button"
                    onClick={() => setView({ ...view, line: null })}
                    className="ml-auto shrink-0 rounded border border-[#24384a] px-1.5 py-0.5 text-[10.5px] text-[#9db3c7] hover:text-[#cfe3f5]"
                  >
                    看整份
                  </button>
                ) : view.line ? (
                  <button
                    type="button"
                    onClick={() => setView({ ...view, line: view.line })}
                    className="ml-auto shrink-0 rounded border border-[#24384a] px-1.5 py-0.5 text-[10.5px] text-[#9db3c7] hover:text-[#cfe3f5]"
                  >
                    只看这一块
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => setView(null)}
                  className="shrink-0 rounded border border-[#24384a] px-1.5 py-0.5 text-[10.5px] text-[#9db3c7] hover:text-[#cfe3f5]"
                >
                  关闭
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto">
                <SyntaxCode
                  code={windowed ? windowed.code : view.code}
                  startLine={windowed ? windowed.start : 1}
                  highlightRange={view.line ? { start: Math.max(1, view.line - 1), end: view.line + 1 } : undefined}
                />
              </div>
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
}

function FeatureDetail(props: {
  feature: Feature;
  onPick: (id: string) => void;
  onOpenCode: (path: string, line: number | null) => void;
  codeHint: string;
}) {
  const { feature, onPick, onOpenCode, codeHint } = props;
  const appName = MAP.apps.find((a) => a.id === feature.app)?.name ?? feature.app;
  const pageName = MAP.apps.find((a) => a.id === feature.app)?.pages.find((p) => p.id === feature.page)?.name ?? "后台/公共";
  const dupOf = MAP.duplicates[baseName(feature.server)] ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-[17px] font-medium text-[#e3eefa]">{feature.name}</h2>
        <p className="mt-1 text-[11.5px] text-[#69809a]">
          {appName} · {pageName} · <span className="font-mono text-[#8aa0b5]">{feature.method} {feature.route}</span>
          {codeHint ? <span className="ml-2 text-[#c0503a]">{codeHint}</span> : null}
        </p>
        <p className="mt-2 text-[12.5px] leading-[1.8] text-[#9db3c7]">
          {feature.tables.length > 0
            ? `做这件事，系统会去读写 ${feature.tables.join("、")} 这些数据。`
            : `做这件事，系统主要走 ${feature.route} 这个接口，不动业务数据表。`}
        </p>
      </div>

      <section className="rounded border border-[#1a2836] bg-[#0c151d]">
        <h3 className="border-b border-[#1a2836] px-3 py-1.5 text-[11.5px] text-[#69809a]">这条功能走过的代码</h3>
        <div className="divide-y divide-[#121c26]">
          <div className="px-3 py-2">
            <p className="text-[11px] text-[#4e6478]">① 页面上的位置</p>
            {feature.front.length > 0 ? (
              feature.front.map((site) => (
                <button
                  key={`${site.file}-${site.line}`}
                  type="button"
                  onClick={() => onOpenCode(site.file, site.line)}
                  className="mt-1 block w-full truncate text-left font-mono text-[11.5px] text-[#7fb3ff] hover:underline"
                >
                  {short(site.file)} 第 {site.line} 行
                </button>
              ))
            ) : (
              <p className="mt-1 text-[11.5px] text-[#4e6478]">没有页面按钮，由别的功能或后台任务调用</p>
            )}
          </div>
          <div className="px-3 py-2">
            <p className="text-[11px] text-[#4e6478]">② 后端接口</p>
            <button
              type="button"
              onClick={() => onOpenCode(feature.server, 1)}
              className="mt-1 block w-full truncate text-left font-mono text-[11.5px] text-[#7fb3ff] hover:underline"
            >
              {short(feature.server)}
            </button>
          </div>
          <div className="px-3 py-2">
            <p className="text-[11px] text-[#4e6478]">③ 用到的公共件</p>
            {feature.utils.length > 0 ? (
              feature.utils.map((util) => (
                <button
                  key={util}
                  type="button"
                  onClick={() => onOpenCode(util, 1)}
                  className="mt-1 block w-full truncate text-left font-mono text-[11.5px] text-[#7fb3ff] hover:underline"
                >
                  {short(util)}
                </button>
              ))
            ) : (
              <p className="mt-1 text-[11.5px] text-[#4e6478]">没引用额外的工具文件</p>
            )}
          </div>
        </div>
      </section>

      <section className="rounded border border-[#1a2836] bg-[#0c151d]">
        <h3 className="border-b border-[#1a2836] px-3 py-1.5 text-[11.5px] text-[#69809a]">改这里会连带影响谁</h3>
        <div className="space-y-2 px-3 py-2.5">
          {feature.tables.length === 0 && feature.utils.length === 0 ? (
            <p className="text-[11.5px] text-[#4e6478]">这个功能不碰公共数据表，改它基本只影响自己。</p>
          ) : null}

          {feature.tables.map((table) => {
            const others = byTableOthers(table, feature.id);
            return (
              <div key={table}>
                <p className="text-[11.5px] text-[#cfe3f5]">
                  动 <span className="font-mono text-[#e0b877]">{table}</span> 这张表，会影响 {others.length + 1} 个功能
                  {others.length > 0 ? <span className="text-[#69809a]">（除本功能外还有 {others.length} 个）</span> : null}
                </p>
                {others.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {others.map((other) => (
                      <button
                        key={other.id}
                        type="button"
                        onClick={() => onPick(other.id)}
                        className="rounded border border-[#24384a] px-1.5 py-0.5 text-[11px] text-[#9db3c7] hover:border-[#3a5a74] hover:text-[#cfe3f5]"
                      >
                        {other.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}

          {feature.utils.map((util) => {
            const others = byUtilsOthers(util, feature.id);
            return (
              <div key={util}>
                <p className="text-[11.5px] text-[#cfe3f5]">
                  改 <span className="font-mono text-[#8fbfe8]">{short(util)}</span>，会影响 {others.length + 1} 个功能
                </p>
                {others.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {others.map((other) => (
                      <button
                        key={other.id}
                        type="button"
                        onClick={() => onPick(other.id)}
                        className="rounded border border-[#24384a] px-1.5 py-0.5 text-[11px] text-[#9db3c7] hover:border-[#3a5a74] hover:text-[#cfe3f5]"
                      >
                        {other.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}

          {dupOf.length >= 2 ? (
            <p className="rounded border border-[#3a3320] bg-[#1a1710] px-2 py-1.5 text-[11.5px] leading-[1.7] text-[#d8b96a]">
              注意：<span className="font-mono">{baseName(feature.server)}</span> 这个文件在 {dupOf.join("、")}{" "}
              里各有一份，是复制品。改这里不会自动同步过去，那几处要各改一遍。
            </p>
          ) : null}
        </div>
      </section>

      <p className="pb-2 text-[11px] text-[#3f5567]">
        数据来自云电脑上的真实源码（{MAP.features.length} 个功能、{Object.keys(MAP.byTable).length} 张表），代码改动后重新生成即可更新。
      </p>
    </div>
  );
}
